// Match history storage and learning engine
// Stores what opponents actually brought/led vs what we predicted

import { ALL_POKEMON } from './teams';
import { getEffectiveness } from './typeChart';
import { getBaseStats } from './baseStats';

const HISTORY_KEY = 'pokemon-champions-match-history';

export function loadHistory() {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
  } catch { return []; }
}

export function saveMatch(match) {
  const history = loadHistory();
  history.push({ ...match, timestamp: Date.now() });
  // Keep last 200 matches
  if (history.length > 200) history.splice(0, history.length - 200);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
}

export function clearHistory() {
  localStorage.removeItem(HISTORY_KEY);
}

/**
 * Given historical matches, compute per-Pokemon bring/lead rate adjustments.
 * Returns a map: pokemonName -> { bringRate, leadRate, sampleSize }
 *
 * bringRate: how often this Pokemon was actually brought when on the opponent team
 *            relative to how often we predicted it. >1 = brought more than expected.
 * leadRate:  how often this Pokemon was in the lead slot when brought.
 */
export function computeLearnedAdjustments(history) {
  const stats = {}; // name -> { timesSeen, timesBrought, timesLed }

  for (const match of history) {
    if (!match.opponentTeam || !match.opponentBrought) continue;

    for (const pokemon of match.opponentTeam) {
      const name = pokemon.name || pokemon;
      if (!stats[name]) stats[name] = { timesSeen: 0, timesBrought: 0, timesLed: 0 };
      stats[name].timesSeen++;
    }

    for (const name of match.opponentBrought) {
      if (!stats[name]) stats[name] = { timesSeen: 0, timesBrought: 0, timesLed: 0 };
      stats[name].timesBrought++;
    }

    if (match.opponentLeads) {
      for (const name of match.opponentLeads) {
        if (!stats[name]) stats[name] = { timesSeen: 0, timesBrought: 0, timesLed: 0 };
        stats[name].timesLed++;
      }
    }
  }

  const adjustments = {};
  for (const [name, s] of Object.entries(stats)) {
    if (s.timesSeen < 1) continue;
    adjustments[name] = {
      bringRate: s.timesBrought / s.timesSeen, // 0-1: how often brought when available
      leadRate: s.timesBrought > 0 ? s.timesLed / s.timesBrought : 0, // 0-1: how often led when brought
      sampleSize: s.timesSeen,
    };
  }

  return adjustments;
}

/**
 * Formats history stats for display
 */
export function getHistoryStats(history) {
  return {
    totalMatches: history.length,
    recentMatches: history.slice(-10),
  };
}

// ============================================================
// Analytics Engine — designed for future Firebase migration
// All functions work on the history array so they can be swapped
// to query a database instead of localStorage
// ============================================================

/**
 * Overall win/loss record for a specific team.
 * Returns { wins, losses, forfeitWins, forfeitLosses, total, winRate }
 */
export function getTeamRecord(history, teamName) {
  const matches = teamName
    ? history.filter(m => m.yourTeamName === teamName || m.yourTeam === teamName)
    : history;

  const wins = matches.filter(m => m.result === 'win').length;
  const losses = matches.filter(m => m.result === 'loss').length;
  const forfeitWins = matches.filter(m => m.result === 'forfeit-win').length;
  const forfeitLosses = matches.filter(m => m.result === 'forfeit-loss').length;
  const total = matches.length;
  const totalWins = wins + forfeitWins;
  const totalLosses = losses + forfeitLosses;
  // Win rate is based on decided matches (wins + losses) — matches with other
  // results (draws, in-progress) don't count in the denominator.
  const decided = totalWins + totalLosses;
  const winRate = decided > 0 ? Math.round((totalWins / decided) * 100) : 0;

  return { wins, losses, forfeitWins, forfeitLosses, total, totalWins, totalLosses, winRate, matches };
}

/**
 * Win rate against a specific opponent Pokemon.
 * "How often do we win when this Pokemon is on the enemy team?"
 * Returns { encounters, wins, losses, winRate }
 */
export function getWinRateVsPokemon(history, pokemonName, teamName) {
  const matches = history.filter(m => {
    const isOurTeam = !teamName || m.yourTeamName === teamName || m.yourTeam === teamName;
    const oppHasPokemon = (m.opponentTeam || []).some(p => (p.name || p) === pokemonName);
    return isOurTeam && oppHasPokemon;
  });

  const wins = matches.filter(m => m.result === 'win' || m.result === 'forfeit-win').length;
  const losses = matches.filter(m => m.result === 'loss' || m.result === 'forfeit-loss').length;
  const total = matches.length;
  const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;

  return { encounters: total, wins, losses, winRate };
}

/**
 * Win rate against a specific archetype.
 * Returns { encounters, wins, losses, winRate }
 */
export function getWinRateVsArchetype(history, archetype, teamName) {
  const matches = history.filter(m => {
    const isOurTeam = !teamName || m.yourTeamName === teamName || m.yourTeam === teamName;
    return isOurTeam && m.archetype === archetype;
  });

  const wins = matches.filter(m => m.result === 'win' || m.result === 'forfeit-win').length;
  const losses = matches.length - wins;
  const winRate = matches.length > 0 ? Math.round((wins / matches.length) * 100) : 0;

  return { encounters: matches.length, wins, losses, winRate };
}

/**
 * Which of YOUR Pokemon win the most when brought?
 * Returns array of { name, timesBrought, winsWhenBrought, winRate }
 */
export function getYourPokemonWinRates(history, teamName) {
  const pokemonStats = {};

  for (const m of history) {
    if (teamName && m.yourTeamName !== teamName && m.yourTeam !== teamName) continue;
    const isWin = m.result === 'win' || m.result === 'forfeit-win';
    const brought = m.yourBrought || [];

    for (const name of brought) {
      if (!pokemonStats[name]) pokemonStats[name] = { timesBrought: 0, wins: 0 };
      pokemonStats[name].timesBrought++;
      if (isWin) pokemonStats[name].wins++;
    }
  }

  return Object.entries(pokemonStats)
    .map(([name, s]) => ({
      name,
      timesBrought: s.timesBrought,
      winsWhenBrought: s.wins,
      winRate: s.timesBrought > 0 ? Math.round((s.wins / s.timesBrought) * 100) : 0,
    }))
    .sort((a, b) => b.timesBrought - a.timesBrought);
}

/**
 * Which OPPONENT Pokemon give us the most trouble?
 * Returns array of { name, encounters, wins, losses, winRate }
 */
export function getOpponentPokemonThreatLevel(history, teamName) {
  const pokemonStats = {};

  for (const m of history) {
    if (teamName && m.yourTeamName !== teamName && m.yourTeam !== teamName) continue;
    const isWin = m.result === 'win' || m.result === 'forfeit-win';
    const oppBrought = m.opponentBrought || [];

    for (const name of oppBrought) {
      if (!pokemonStats[name]) pokemonStats[name] = { encounters: 0, wins: 0, losses: 0 };
      pokemonStats[name].encounters++;
      if (isWin) pokemonStats[name].wins++;
      else pokemonStats[name].losses++;
    }
  }

  return Object.entries(pokemonStats)
    .map(([name, s]) => ({
      name,
      encounters: s.encounters,
      wins: s.wins,
      losses: s.losses,
      winRate: s.encounters > 0 ? Math.round((s.wins / s.encounters) * 100) : 0,
    }))
    .sort((a, b) => a.winRate - b.winRate); // worst matchups first
}

/**
 * Matchup history between your team and a specific opponent team composition.
 * Groups by opponent team (sorted names), returns per-team records.
 */
export function getTeamVsTeamHistory(history, teamName) {
  const matchups = {};

  for (const m of history) {
    if (teamName && m.yourTeamName !== teamName && m.yourTeam !== teamName) continue;
    const oppKey = (m.opponentTeam || []).map(p => p.name || p).sort().join(',');
    if (!oppKey) continue;

    if (!matchups[oppKey]) {
      matchups[oppKey] = {
        opponentTeam: (m.opponentTeam || []).map(p => p.name || p).sort(),
        wins: 0, losses: 0, total: 0, matches: []
      };
    }
    matchups[oppKey].total++;
    const isWin = m.result === 'win' || m.result === 'forfeit-win';
    if (isWin) matchups[oppKey].wins++;
    else matchups[oppKey].losses++;
    matchups[oppKey].matches.push(m);
  }

  return Object.values(matchups)
    .map(mu => ({
      ...mu,
      winRate: mu.total > 0 ? Math.round((mu.wins / mu.total) * 100) : 0,
    }))
    .sort((a, b) => b.total - a.total);
}

/**
 * Summary dashboard data for a team.
 * Returns everything needed to render a stats overview.
 */
export function getTeamAnalytics(history, teamName) {
  const record = getTeamRecord(history, teamName);
  const yourPokemon = getYourPokemonWinRates(history, teamName);
  const oppThreats = getOpponentPokemonThreatLevel(history, teamName);
  const teamMatchups = getTeamVsTeamHistory(history, teamName);

  // Archetype breakdown
  const archetypes = {};
  for (const m of record.matches) {
    const arch = m.archetype || 'Unknown';
    if (!archetypes[arch]) archetypes[arch] = { wins: 0, losses: 0, total: 0 };
    archetypes[arch].total++;
    const isWin = m.result === 'win' || m.result === 'forfeit-win';
    if (isWin) archetypes[arch].wins++;
    else archetypes[arch].losses++;
  }
  const archetypeBreakdown = Object.entries(archetypes)
    .map(([name, s]) => ({
      name,
      ...s,
      winRate: s.total > 0 ? Math.round((s.wins / s.total) * 100) : 0,
    }))
    .sort((a, b) => b.total - a.total);

  // Recent form (last 10)
  const recent = record.matches.slice(-10);
  const recentWins = recent.filter(m => m.result === 'win' || m.result === 'forfeit-win').length;

  return {
    record,
    yourPokemon,
    oppThreats: oppThreats.slice(0, 10),
    teamMatchups: teamMatchups.slice(0, 10),
    archetypeBreakdown,
    recentForm: { matches: recent.length, wins: recentWins, winRate: recent.length > 0 ? Math.round((recentWins / recent.length) * 100) : 0 },
  };
}

// ============================================================
// Optimal Team Builder
// ============================================================
// Uses match history to pick the six Pokemon most likely to win the user's
// matches. Combines three signals:
//   1. Empirical per-Pokemon win rate (how often YOU win when bringing X)
//   2. Threat coverage (which of YOUR Pokemon counter opponents that beat you)
//   3. Sample-size weighting so a 1-match wonder doesn't dominate
//
// Returns:
//   {
//     team: [{ name, types, winRate, timesBrought, reasons }...],
//     topThreats: [{ name, winRate, encounters }...],
//     coveredThreats: number,
//     totalThreats: number,
//     minSamples: number,
//     lowDataWarning: boolean,
//   }

function canHitSE(attacker, defender) {
  return (attacker.types || []).some(t => getEffectiveness(t, defender.types) >= 2);
}

// Pokemon whose offensive type is effectively unbounded (Protean, Libero, etc.).
// "Walling" them based on defensive types is meaningless — they pick a SE move.
const PROTEAN_LIKE = new Set(['Greninja', 'Cinderace']);

function walls(defender, attacker) {
  if (PROTEAN_LIKE.has(attacker.name)) return false;
  return (attacker.types || []).every(t => getEffectiveness(t, defender.types) <= 0.5);
}

// Wilson lower-bound win rate — rewards high raw rate AND high sample size.
// Small-sample 100% win rate gets penalized vs a 70% win rate over many games.
function wilsonLowerBound(wins, total) {
  if (total === 0) return 0;
  const z = 1.64; // 90% confidence
  const p = wins / total;
  const denom = 1 + (z * z) / total;
  const center = p + (z * z) / (2 * total);
  const margin = z * Math.sqrt((p * (1 - p) + (z * z) / (4 * total)) / total);
  return ((center - margin) / denom) * 100;
}

// Reg M-A current-season meta — most-used singles Pokemon, in rank order.
// Source: pasted by user from in-game usage stats. Update each season.
// Note on Protean Greninja: its STAB matches whatever move it just used, so
// type-coverage analysis underestimates how much damage it does. The
// algorithm still picks for Water/Dark coverage; for live battles, treat it
// as effectively having every type and lean on priority + raw speed control.
export const META_THREATS_SINGLES = [
  'Garchomp', 'Primarina', 'Charizard', 'Corviknight', 'Archaludon',
  'Hippowdon', 'Gengar', 'Scizor', 'Aegislash', 'Dragonite',
  'Meowscarada', 'Kingambit', 'Mimikyu', 'Lopunny', 'Basculegion',
  'Rotom-Wash', 'Hydreigon', 'Glimmora', 'Delphox', 'Umbreon',
  // Tier-2 common-but-deadly picks (rank inferred — adjust as new usage data arrives)
  'Greninja', 'Tyranitar', 'Volcarona', 'Excadrill', 'Tinkaton',
  'Weavile', 'Skeledirge', 'Slowking-Galarian', 'Garganacl', 'Conkeldurr',
];

export function computeOptimalTeam(history, options = {}) {
  const teamSize = options.teamSize ?? 6;
  const metaList = options.metaThreats || META_THREATS_SINGLES;

  // --- Build the threat set from the current meta. Each gets a usage weight
  // derived from rank (#1 = full weight, last = small fraction). If we have
  // user history vs that threat, layer that in: bad win rate inflates weight,
  // good win rate deflates it.
  const N = metaList.length;
  const userThreatStats = {};
  for (const m of history) {
    const isWin = m.result === 'win' || m.result === 'forfeit-win';
    for (const name of m.opponentBrought || []) {
      if (!userThreatStats[name]) userThreatStats[name] = { encounters: 0, wins: 0 };
      userThreatStats[name].encounters++;
      if (isWin) userThreatStats[name].wins++;
    }
  }

  const topThreats = metaList.map((name, idx) => {
    const meta = ALL_POKEMON.find(p => p.name === name);
    if (!meta) return null;
    const baseWeight = (N - idx) / N; // 1.0 down to 1/N
    const personal = userThreatStats[name];
    let weight = baseWeight;
    let userWinRate = null;
    let encounters = 0;
    if (personal && personal.encounters >= 2) {
      encounters = personal.encounters;
      userWinRate = Math.round((personal.wins / personal.encounters) * 100);
      // Scale: 0% win rate → 1.5× weight, 100% → 0.6× weight.
      const scale = 1.5 - (userWinRate / 100) * 0.9;
      weight *= scale;
    }
    return {
      name,
      types: meta.types,
      rank: idx + 1,
      weight,
      baseWeight,
      encounters,
      winRate: userWinRate,
    };
  }).filter(Boolean);

  // --- Build personal stats so we can give bonuses for Pokemon the user has
  // proven results with (without making history a hard requirement).
  const personalStats = {};
  for (const m of history) {
    const isWin = m.result === 'win' || m.result === 'forfeit-win';
    for (const name of m.yourBrought || []) {
      if (!personalStats[name]) personalStats[name] = { brought: 0, wins: 0 };
      personalStats[name].brought++;
      if (isWin) personalStats[name].wins++;
    }
  }

  // --- Score every Pokemon in the Champions roster against the meta.
  const candidates = ALL_POKEMON.map(p => {
    const personal = personalStats[p.name];
    const wilsonScore = personal && personal.brought >= 2
      ? wilsonLowerBound(personal.wins, personal.brought)
      : 0;
    const base = getBaseStats(p.name);
    const bst = base ? (base.hp + base.atk + base.def + base.spa + base.spd + base.spe) : 400;
    return {
      name: p.name,
      types: p.types,
      timesBrought: personal?.brought || 0,
      wins: personal?.wins || 0,
      winRate: personal && personal.brought > 0
        ? Math.round((personal.wins / personal.brought) * 100)
        : 0,
      wilsonScore,
      bst,
    };
  });

  // Coverage scoring: SE = 1.0× weight, walls = 0.6× weight.
  // Diminishing returns: a threat already covered N times contributes
  // weight × 0.5^N to subsequent picks.
  const coverageCount = {}; // threatName -> times answered
  const selected = [];
  const remaining = [...candidates];

  const scoreCandidate = (cand) => {
    let coveragePoints = 0;
    const coveredNames = [];
    for (const threat of topThreats) {
      const se = canHitSE(cand, threat);
      const wall = walls(cand, threat);
      if (!se && !wall) continue;
      const intensity = se ? 1.0 : 0.6;
      const already = coverageCount[threat.name] || 0;
      const dim = Math.pow(0.5, already);
      coveragePoints += threat.weight * intensity * dim;
      coveredNames.push({ name: threat.name, kind: se ? 'SE' : 'wall', firstAnswer: already === 0 });
    }
    // BST kicker — tiny weight that prefers stronger Pokemon when coverage
    // ties. Caps so a 600 BST legendary doesn't outweigh real coverage.
    const bstBonus = (cand.bst - 400) / 800; // ~0 for 400, ~0.25 for 600
    // Personal Wilson bonus — *very* small to keep this meta-driven, not
    // history-driven. Without this, picks 5/6 (where coverage diminishes)
    // were getting dominated by anything the user had ever played.
    const personalBonus = cand.wilsonScore * 0.25;
    return {
      points: coveragePoints + bstBonus + personalBonus,
      covered: coveredNames,
    };
  };

  while (selected.length < teamSize && remaining.length > 0) {
    let best = null;
    let bestData = null;
    let bestScore = -Infinity;
    for (const cand of remaining) {
      const data = scoreCandidate(cand);
      if (data.points > bestScore) {
        bestScore = data.points;
        best = cand;
        bestData = data;
      }
    }
    if (!best || bestScore <= 0) break;

    const newAnswers = bestData.covered.filter(c => c.firstAnswer).map(c => c.name);
    const followups = bestData.covered.filter(c => !c.firstAnswer).map(c => c.name);

    const reasons = [];
    if (newAnswers.length > 0) {
      reasons.push(`Answers ${newAnswers.slice(0, 6).join(', ')}${newAnswers.length > 6 ? '…' : ''}`);
    }
    if (followups.length > 0 && newAnswers.length < 4) {
      reasons.push(`Backs up coverage on ${followups.slice(0, 4).join(', ')}${followups.length > 4 ? '…' : ''}`);
    }
    if (best.timesBrought >= 2) {
      reasons.push(`${best.winRate}% personal win rate (${best.wins}-${best.timesBrought - best.wins} in ${best.timesBrought})`);
    }

    for (const c of bestData.covered) {
      coverageCount[c.name] = (coverageCount[c.name] || 0) + 1;
    }

    selected.push({ ...best, reasons, _covered: bestData.covered.map(c => c.name) });
    const idx = remaining.findIndex(r => r.name === best.name);
    if (idx >= 0) remaining.splice(idx, 1);
  }

  const coveredThreats = topThreats.filter(t => (coverageCount[t.name] || 0) > 0).length;

  return {
    team: selected,
    topThreats,
    coveredThreats,
    totalThreats: topThreats.length,
    minSamples: 2,
    lowDataWarning: history.length < 5,
    totalMatches: history.length,
  };
}
